import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { Link } from 'react-router-dom'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import ImageFull, { ImageSizes } from 'components/ui/image-full/ImageFull'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import { dateParse } from 'helpers/date.helper'
import { CourseTypeDic } from 'enum/course-type.enum'
import * as fromAccessActions from 'store/actions/access.action'
import { SynchronousCourseTypes } from 'enum/synchronousCourseTypes.enum'
import { MonitoringCourseProps, PathRoutes } from 'enum/routes'
import Loader from 'components/ui/loader/Loader'
import { useTranslationRankmi } from 'helpers/translations.helper'

import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import { Tags, Statistics } from './components'
import { prefix } from './constants'
import styles from './Course.module.scss'
import Wrapper from 'components/reusables/wrapper'

export const Course: React.FC = () => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({
    prefix,
  })
  const dispatch = useDispatch()
  const { id = 0 } = useParams<MonitoringCourseProps>()

  const { course, loading } = useSelector(({ courses }: IMainState) => courses)
  const lessons = course?.lessons || []

  useEffect(() => {
    dispatch({ type: fromAccessActions.CAN_COURSE, payload: id })
    dispatch({ type: fromActions.GET_COURSE, payload: id })
  }, [dispatch, id])

  const prefixContent = 'courses.show.content'
  const isIntegrationCourse = course['integrationCourseLink']

  const translateCourseType = CourseTypeDic[Number(course && course.courseType)]
  const isSynchronous = !!course.synchronousCourse
  const { synchronousCourseType } = {
    ...course.synchronousCourse,
  }
  const type = React.useMemo(() => {
    const labels = []
    if (translateCourseType)
      labels.push(t(`commons.course.type.${translateCourseType}`))
    if (isSynchronous) {
      const text =
        synchronousCourseType && SynchronousCourseTypes[synchronousCourseType]
      if (text) labels.push(t(`commons.course.type.synchronousTypes.${text}`))
    }
    return labels.join(' - ')
  }, [isSynchronous, synchronousCourseType, t, translateCourseType])

  const displayHoursCourse = () => {
    if (isIntegrationCourse && (course.hours === 0 || course.hours === null)) {
      return '-'
    }

    return tRelative(`data.hours`, {
      count: course.hours || 0,
    })
  }

  return (
    <Wrapper shadow={true}>
      {(loading && (
        <div className="d-flex justify-content-center">
          <Loader color />
        </div>
      )) || (
        <Row style={{ marginTop: -11 }}>
          <Col>
            <Breadcrumb separator=">">
              <Link
                component={(props: any) => (
                  <BreadcrumbItem
                    text={t('monitoring.course.breadcrumb')}
                    onClick={(e) => {
                      e.preventDefault()
                      props?.navigate()
                    }}
                    href={props.href}
                  />
                )}
                to={PathRoutes.MONITORING_COURSES}
              />
              <BreadcrumbItem text={course.name} />
            </Breadcrumb>
            <Row className={'mt-4 mb-2'}>
              <Col sm={12} md={12} lg={8}>
                <Row>
                  <Col md={12}>
                    <h1 className="font-weight-bold">{course.name}</h1>
                  </Col>
                  <Col md={12}>
                    <Row className={'justify-content-between'}>
                      <Col sm={6} md={3} lg={2}>
                        <Row>
                          <Col md={12}>
                            <p className={styles.courseDataTitle}>
                              {t(`${prefixContent}.accessType`)}
                            </p>
                            <div className={classNames(styles.courseData)}>
                              {type}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.startDate`)}
                        </p>
                        <p className={styles.courseData}>
                          {t(dateParse(course.startDate))}
                        </p>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.untilDate`)}
                        </p>
                        <p className={styles.courseData}>
                          {t(dateParse(course.untilDate))}
                        </p>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={`${styles.courseDataTitle} mb-2`}>
                          {t(`${prefixContent}.forProfiles`)}
                        </p>
                        <Tags
                          id="optionalProfiles"
                          profiles={course.optionalProfiles}
                        />
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={`${styles.courseDataTitle} mb-2`}>
                          {t(`${prefixContent}.forMandatoryProfiles`)}
                        </p>
                        <Tags
                          id="requiredProfiles"
                          profiles={course.requiredProfiles}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col md={12}>
                    <Row>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.lessons`)}
                        </p>
                        <p className={styles.courseData}>
                          {isIntegrationCourse
                            ? '-'
                            : tRelative(`data.lessons`, {
                                count: lessons.length || 0,
                              })}
                        </p>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.tasks`)}
                        </p>
                        <p className={styles.courseData}>
                          {isIntegrationCourse
                            ? '-'
                            : tRelative(`data.tasks`, {
                                count: course.tasksCount,
                              })}
                        </p>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.surveys`)}
                        </p>
                        <p className={styles.courseData}>
                          {isIntegrationCourse
                            ? '-'
                            : tRelative(`data.surveys`, {
                                count: course.surveysCount,
                              })}
                        </p>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.evaluations`)}
                        </p>
                        <p className={styles.courseData}>
                          {isIntegrationCourse
                            ? '-'
                            : tRelative(`data.evaluations`, {
                                count: course.evaluationsCount,
                              })}
                        </p>
                      </Col>
                      <Col sm={6} md={3} lg={2}>
                        <p className={styles.courseDataTitle}>
                          {t(`${prefixContent}.hours`)}
                        </p>
                        <p className={styles.courseData}>
                          {displayHoursCourse()}
                        </p>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col sm={12} md={12} lg={4}>
                <div className={styles.banner}>
                  <ImageFull
                    sizes={ImageSizes.Small}
                    src={course.bannerUrl}
                    imageHandlerProps={{
                      height: 490,
                    }}
                  />
                </div>
                <ExternalCourseIndicator
                  origin="MONITOR_COURSE_DETAIL"
                  url={course['integrationCourseLink']}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <h1 className="font-weight-bold">
            {t('monitoring.course.statistics.title')}
          </h1>
        </Col>
        <Col xs={12}>
          <Statistics course={id} isSynchronous={isSynchronous} />
        </Col>
      </Row>
    </Wrapper>
  )
}

export default Course
